"use client";

import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import type {ScriptDraft} from "@/services/wingman";

export type ArtifactData =
   | {kind: "approval"; id: string; title: string}
   | {kind: "workflows"; id: string; title: string}
   | {
        kind: "binary";
        id: string;
        fileName: string;
        mimeType: string;
        /** Base64 payload; empty when the file lives at `url` instead. */
        base64: string;
        /** Remote file location (e.g. signed storage URL) when not inlined in `base64`. */
        url?: string;
     }
   | {
        kind: "generated";
        id: string;
        fileName: string;
        mimeType: string;
        downloadUrl: string;
     }
   | {
        /** Workspace building block: a script draft proposed by the assistant. */
        kind: "script_draft";
        /** Stable identity — the tool callId that produced the draft. */
        id: string;
        draft: ScriptDraft;
     };

type ChatMessageSender = (text: string) => void;

/** Outcome of saving a workspace draft, kept so a reopened draft shows its saved state. */
export type SavedDraftResult = {
   scriptId: string;
   detailHref: string;
};

type ArtifactPanelValue = {
   /** False on standalone renderers without a panel provider. */
   available: boolean;
   activeArtifact: ArtifactData | null;
   openArtifact: (artifact: ArtifactData) => void;
   closeArtifact: (id?: string) => void;
   approvalPanelHost: HTMLDivElement | null;
   setApprovalPanelHost: (host: HTMLDivElement | null) => void;
   workflowPanelHost: HTMLDivElement | null;
   setWorkflowPanelHost: (host: HTMLDivElement | null) => void;
   /**
    * Ids that have already auto-opened on this chat surface. Lives in the
    * provider (not the bubble) so a bubble remount — e.g. the trailing-turn
    * anchor regrouping messages — doesn't re-pop the panel for an artifact
    * the user already saw (and possibly closed).
    */
   autoOpenedIds: Set<string>;
   /**
    * The open panel replaces the conversation instead of sitting beside it
    * (the dock). Artifacts then open only when asked, so a reply never hides
    * the conversation it arrived in.
    */
   coversConversation: boolean;
   /**
    * Sends a message into the conversation that hosts this workspace. The
    * panel is a sibling of the chat view, so the chat surface registers its
    * sender here and workspace actions (e.g. "script saved") report outcomes
    * back into the conversation through it. No-op until a sender registers.
    */
   sendChatMessage: ChatMessageSender;
   registerChatMessageSender: (sender: ChatMessageSender | null) => void;
   /**
    * Save outcomes keyed by artifact id (the draft's callId). Lives in the
    * provider so closing and reopening a draft shows "saved" instead of a
    * save button that would create a duplicate. Per chat-surface lifetime;
    * restoring across page reloads is the Story 6 conversation-reload work.
    */
   savedDrafts: Map<string, SavedDraftResult>;
};

const ArtifactPanelContext = createContext<ArtifactPanelValue | undefined>(undefined);

export const ArtifactPanelProvider: React.FC<{children: React.ReactNode; coversConversation?: boolean}> = ({
   children,
   coversConversation = false,
}) => {
   const [activeArtifact, setActiveArtifact] = useState<ArtifactData | null>(null);
   const [approvalPanelHost, setApprovalPanelHost] = useState<HTMLDivElement | null>(null);
   const [workflowPanelHost, setWorkflowPanelHost] = useState<HTMLDivElement | null>(null);
   const autoOpenedIdsRef = useRef<Set<string>>(new Set());
   const chatSenderRef = useRef<ChatMessageSender | null>(null);
   const savedDraftsRef = useRef<Map<string, SavedDraftResult>>(new Map());

   const openArtifact = useCallback((artifact: ArtifactData) => {
      setActiveArtifact(artifact);
   }, []);

   const closeArtifact = useCallback((id?: string) => {
      setActiveArtifact(current => !id || current?.id === id ? null : current);
   }, []);

   const registerChatMessageSender = useCallback((sender: ChatMessageSender | null) => {
      chatSenderRef.current = sender;
   }, []);

   const sendChatMessage = useCallback((text: string) => {
      chatSenderRef.current?.(text);
   }, []);

   const value = useMemo(
      () => ({
         available: true,
         activeArtifact,
         approvalPanelHost,
         setApprovalPanelHost,
         workflowPanelHost,
         setWorkflowPanelHost,
         openArtifact,
         closeArtifact,
         autoOpenedIds: autoOpenedIdsRef.current,
         coversConversation,
         sendChatMessage,
         registerChatMessageSender,
         savedDrafts: savedDraftsRef.current,
      }),
      [activeArtifact, approvalPanelHost, workflowPanelHost, coversConversation, openArtifact, closeArtifact, sendChatMessage, registerChatMessageSender]
   );

   return (
      <ArtifactPanelContext.Provider value={value}>{children}</ArtifactPanelContext.Provider>
   );
};

// Shared singleton so bubbles rendered outside a provider (e.g. preview surfaces
// we haven't wrapped yet) degrade gracefully instead of throwing. Panel stays
// closed and `openArtifact` becomes a no-op. We warn in every environment (not
// just dev) because a silent no-op in prod looks like a mysterious UX bug.
const FALLBACK_VALUE: ArtifactPanelValue = {
   available: false,
   activeArtifact: null,
   approvalPanelHost: null,
   setApprovalPanelHost: () => {},
   workflowPanelHost: null,
   setWorkflowPanelHost: () => {},
   openArtifact: () => {
      console.warn(
         "useArtifactPanel: openArtifact called outside an ArtifactPanelProvider — " +
            "artifact will not open. Wrap this surface in <ArtifactPanelProvider>."
      );
   },
   closeArtifact: () => {},
   autoOpenedIds: new Set(),
   coversConversation: false,
   sendChatMessage: () => {
      console.warn(
         "useArtifactPanel: sendChatMessage called outside an ArtifactPanelProvider — " +
            "message will not be sent. Wrap this surface in <ArtifactPanelProvider>."
      );
   },
   registerChatMessageSender: () => {},
   savedDrafts: new Map(),
};

/**
 * Returns the active artifact + open/close handlers.
 *
 * Prefer calling inside an `ArtifactPanelProvider`. Outside a provider the
 * hook returns a no-op fallback (panel stays closed) and logs a console
 * warning on every call to `openArtifact`, so bubbles that render on surfaces
 * without the provider degrade instead of crashing the render.
 */
export const useArtifactPanel = (): ArtifactPanelValue => {
   const ctx = useContext(ArtifactPanelContext);
   return ctx ?? FALLBACK_VALUE;
};

/**
 * Opens `build()` via `openArtifact` once per distinct `artifactId`.
 * Opened ids are tracked on the provider (shared across bubbles) so the panel
 * opens once per artifact for the surface's lifetime, surviving bubble
 * remounts caused by chat-list regrouping.
 */
export const useAutoOpenArtifact = (
   enabled: boolean,
   artifactId: string,
   build: () => ArtifactData,
   openArtifact: (artifact: ArtifactData) => void
): void => {
   const {autoOpenedIds, coversConversation} = useArtifactPanel();
   const buildRef = useRef(build);
   buildRef.current = build;

   useEffect(() => {
      if (!enabled || coversConversation) return;
      if (autoOpenedIds.has(artifactId)) return;
      autoOpenedIds.add(artifactId);
      openArtifact(buildRef.current());
   }, [enabled, coversConversation, artifactId, openArtifact, autoOpenedIds]);
};
